import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Globe, Loader2, Square, Circle, RectangleHorizontal,
  AlignLeft, AlignCenter, AlignRight, ImageIcon
} from 'lucide-react';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import {
  ImageUploader, VideoUploader, GalleryEditor, ButtonsEditor,
  SortableFeaturedProduct, FocalPointPicker
} from './Primitives';
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors
} from '@dnd-kit/core';
import {
  SortableContext, verticalListSortingStrategy, arrayMove
} from '@dnd-kit/sortable';

const API = process.env.REACT_APP_BACKEND_URL + '/api';


export function HeroEditor({ block, updateBlock }) {
  const u = (f, v) => updateBlock(block.id, f, v);
  return (
    <div className="space-y-3">
      <ImageUploader value={block.content.image_url} onChange={(v) => u('image_url', v)} label="Background" blockId={block.id + '-hero'} linkValue={block.content.image_link} onLinkChange={(v) => u('image_link', v)} />
      {block.content.image_url && (
        <FocalPointPicker
          imageUrl={block.content.image_url}
          value={block.content.focus_point}
          onChange={(v) => u('focus_point', v)}
          label="Image Focal Point"
          aspect="16 / 7"
        />
      )}
      <VideoUploader value={block.content.video_url} onChange={(v) => u('video_url', v)} blockId={block.id + '-herovid'} />
      <div><Label className="text-[#9CA3AF] text-xs">Title</Label><Input value={block.content.title || ''} onChange={(e) => u('title', e.target.value)} className="bg-white/5 border-white/10 text-white mt-1 rounded-lg" /></div>
      <div><Label className="text-[#9CA3AF] text-xs">Tagline</Label><Input value={block.content.tagline || ''} onChange={(e) => u('tagline', e.target.value)} className="bg-white/5 border-white/10 text-white mt-1 rounded-lg" /></div>
      <ButtonsEditor buttons={block.content.buttons || []} onChange={(v) => u('buttons', v)} blockId={block.id} />
    </div>
  );
}

export function BannerEditor({ block, updateBlock }) {
  const u = (f, v) => updateBlock(block.id, f, v);
  return (
    <div className="space-y-3">
      <ImageUploader value={block.content.image_url} onChange={(v) => u('image_url', v)} label="Banner Image" blockId={block.id + '-banner'} linkValue={block.content.image_link} onLinkChange={(v) => u('image_link', v)} />
      {block.content.image_url && (
        <FocalPointPicker
          imageUrl={block.content.image_url}
          value={block.content.focus_point}
          onChange={(v) => u('focus_point', v)}
          label="Focal Point — what stays in view"
          aspect="16 / 5"
        />
      )}
      <div><Label className="text-[#9CA3AF] text-xs">Banner Height</Label>
        <select
          value={block.content.height || 'medium'}
          onChange={(e) => u('height', e.target.value)}
          className="w-full bg-white/5 border border-white/10 text-white rounded-lg px-3 py-2 text-sm mt-1"
          data-testid="banner-height-select"
        >
          <option value="short">Short (150px)</option>
          <option value="medium">Medium (260px)</option>
          <option value="tall">Tall (400px)</option>
          <option value="fullscreen">Fullscreen (80vh)</option>
        </select>
      </div>
      <div><Label className="text-[#9CA3AF] text-xs">Text Overlay</Label><Input value={block.content.text || ''} onChange={(e) => u('text', e.target.value)} className="bg-white/5 border-white/10 text-white mt-1 rounded-lg" /></div>
      <ButtonsEditor buttons={block.content.buttons || []} onChange={(v) => u('buttons', v)} blockId={block.id} />
    </div>
  );
}

export function AboutEditor({ block, updateBlock }) {
  const u = (f, v) => updateBlock(block.id, f, v);
  return (
    <div className="space-y-3">
      <ImageUploader value={block.content.image_url} onChange={(v) => u('image_url', v)} label="Portrait" blockId={block.id + '-about'} linkValue={block.content.image_link} onLinkChange={(v) => u('image_link', v)} />
      <div><Label className="text-[#9CA3AF] text-xs">Title</Label><Input value={block.content.title || ''} onChange={(e) => u('title', e.target.value)} className="bg-white/5 border-white/10 text-white mt-1 rounded-lg" /></div>
      <div><Label className="text-[#9CA3AF] text-xs">Bio</Label><Textarea value={block.content.text || ''} onChange={(e) => u('text', e.target.value)} rows={3} className="bg-white/5 border-white/10 text-white mt-1 rounded-lg" /></div>
      <div><Label className="text-[#9CA3AF] text-xs">Vision</Label><Input value={block.content.vision || ''} onChange={(e) => u('vision', e.target.value)} className="bg-white/5 border-white/10 text-white mt-1 rounded-lg" /></div>
      <ButtonsEditor buttons={block.content.buttons || []} onChange={(v) => u('buttons', v)} blockId={block.id} />
    </div>
  );
}

export function GalleryBlockEditor({ block, updateBlock }) {
  const u = (f, v) => updateBlock(block.id, f, v);
  return (
    <div className="space-y-3">
      <div><Label className="text-[#9CA3AF] text-xs">Gallery Title</Label><Input value={block.content.title || ''} onChange={(e) => u('title', e.target.value)} className="bg-white/5 border-white/10 text-white mt-1 rounded-lg" /></div>
      <GalleryEditor images={block.content.images || []} onChange={(v) => u('images', v)} layout={block.content.layout} onLayoutChange={(v) => u('layout', v)} blockId={block.id} />
    </div>
  );
}

export function SignupEditor({ block, updateBlock }) {
  const u = (f, v) => updateBlock(block.id, f, v);
  return (
    <div className="space-y-3">
      <div><Label className="text-[#9CA3AF] text-xs">Title</Label><Input value={block.content.title || ''} onChange={(e) => u('title', e.target.value)} className="bg-white/5 border-white/10 text-white mt-1 rounded-lg" /></div>
      <div><Label className="text-[#9CA3AF] text-xs">Subtitle</Label><Input value={block.content.subtitle || ''} onChange={(e) => u('subtitle', e.target.value)} className="bg-white/5 border-white/10 text-white mt-1 rounded-lg" /></div>
      <ButtonsEditor buttons={block.content.buttons || []} onChange={(v) => u('buttons', v)} blockId={block.id} />
    </div>
  );
}

export function CustomTextEditor({ block, updateBlock }) {
  const u = (f, v) => updateBlock(block.id, f, v);
  const FONT_FAMILIES = [
    { value: '', label: 'Default' },
    { value: 'Syne, sans-serif', label: 'Syne (Display)' },
    { value: 'Inter, sans-serif', label: 'Inter' },
    { value: 'Playfair Display, serif', label: 'Playfair (Serif)' },
    { value: 'Cormorant Garamond, serif', label: 'Cormorant Garamond' },
    { value: 'Space Grotesk, sans-serif', label: 'Space Grotesk' },
    { value: 'DM Serif Display, serif', label: 'DM Serif Display' },
    { value: 'Manrope, sans-serif', label: 'Manrope' },
    { value: 'Lora, serif', label: 'Lora' },
    { value: 'Archivo Black, sans-serif', label: 'Archivo Black' },
  ];
  const SIZE_STEPS = [
    { value: 'sm', label: 'Small' },
    { value: 'md', label: 'Medium' },
    { value: 'lg', label: 'Large' },
    { value: 'xl', label: 'XL' },
    { value: 'xxl', label: 'Huge' },
  ];
  return (
    <div className="space-y-3">
      <div><Label className="text-[#9CA3AF] text-xs">Title</Label><Input value={block.content.title || ''} onChange={(e) => u('title', e.target.value)} className="bg-white/5 border-white/10 text-white mt-1 rounded-lg" /></div>
      <div><Label className="text-[#9CA3AF] text-xs">Text</Label><Textarea value={block.content.text || ''} onChange={(e) => u('text', e.target.value)} rows={3} className="bg-white/5 border-white/10 text-white mt-1 rounded-lg" /></div>
      <div>
        <Label className="text-[#9CA3AF] text-xs">Font Family</Label>
        <select value={block.content.font_family || ''} onChange={(e) => u('font_family', e.target.value)} className="w-full bg-white/5 border border-white/10 text-white rounded-lg px-3 py-2 text-sm mt-1" data-testid="customtext-font-family">
          {FONT_FAMILIES.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
        </select>
      </div>
      <div>
        <Label className="text-[#9CA3AF] text-xs">Size</Label>
        <div className="flex gap-1.5 mt-1">
          {SIZE_STEPS.map(s => (
            <button key={s.value} type="button" onClick={() => u('font_size', s.value)}
              className={`flex-1 px-2 py-1.5 rounded-lg text-xs border transition-all ${(block.content.font_size || 'lg') === s.value ? 'bg-[#8B5CF6]/20 text-[#A78BFA] border-[#8B5CF6]/30' : 'bg-white/5 text-[#64748B] border-white/5 hover:border-white/10'}`}
              data-testid={`customtext-size-${s.value}`}>{s.label}</button>
          ))}
        </div>
      </div>
      <div><Label className="text-[#9CA3AF] text-xs">Alignment</Label>
        <select value={block.content.alignment || 'center'} onChange={(e) => u('alignment', e.target.value)} className="w-full bg-white/5 border border-white/10 text-white rounded-lg px-3 py-2 text-sm mt-1"><option value="left">Left</option><option value="center">Center</option><option value="right">Right</option></select>
      </div>
      <ButtonsEditor buttons={block.content.buttons || []} onChange={(v) => u('buttons', v)} blockId={block.id} />
    </div>
  );
}

export function CustomImageEditor({ block, updateBlock }) {
  const u = (f, v) => updateBlock(block.id, f, v);
  return (
    <div className="space-y-3">
      <ImageUploader value={block.content.image_url} onChange={(v) => u('image_url', v)} label="Image" blockId={block.id + '-cimg'} linkValue={block.content.image_link} onLinkChange={(v) => u('image_link', v)} />
      <div><Label className="text-[#9CA3AF] text-xs">Caption</Label><Input value={block.content.caption || ''} onChange={(e) => u('caption', e.target.value)} className="bg-white/5 border-white/10 text-white mt-1 rounded-lg" /></div>
    </div>
  );
}

export function VideoEditor({ block, updateBlock }) {
  const u = (f, v) => updateBlock(block.id, f, v);
  return (
    <div className="space-y-3">
      <VideoUploader value={block.content.video_url} onChange={(v) => u('video_url', v)} blockId={block.id + '-vid'} />
      <div><Label className="text-[#9CA3AF] text-xs">Title</Label><Input value={block.content.title || ''} onChange={(e) => u('title', e.target.value)} className="bg-white/5 border-white/10 text-white mt-1 rounded-lg" /></div>
      <div><Label className="text-[#9CA3AF] text-xs">Caption</Label><Input value={block.content.caption || ''} onChange={(e) => u('caption', e.target.value)} className="bg-white/5 border-white/10 text-white mt-1 rounded-lg" /></div>
    </div>
  );
}

export function TestimonialEditor({ block, updateBlock }) {
  const u = (f, v) => updateBlock(block.id, f, v);
  return (
    <div className="space-y-3">
      <div><Label className="text-[#9CA3AF] text-xs">Quote</Label><Textarea value={block.content.quote || ''} onChange={(e) => u('quote', e.target.value)} rows={2} className="bg-white/5 border-white/10 text-white mt-1 rounded-lg" /></div>
      <div><Label className="text-[#9CA3AF] text-xs">Author</Label><Input value={block.content.author || ''} onChange={(e) => u('author', e.target.value)} className="bg-white/5 border-white/10 text-white mt-1 rounded-lg" /></div>
      <div><Label className="text-[#9CA3AF] text-xs">Role</Label><Input value={block.content.role || ''} onChange={(e) => u('role', e.target.value)} className="bg-white/5 border-white/10 text-white mt-1 rounded-lg" /></div>
      <ButtonsEditor buttons={block.content.buttons || []} onChange={(v) => u('buttons', v)} blockId={block.id} />
    </div>
  );
}

export function SpacerEditor({ block, updateBlock }) {
  const u = (f, v) => updateBlock(block.id, f, v);
  return (
    <div><Label className="text-[#9CA3AF] text-xs">Height (px)</Label>
      <Input type="number" value={block.content.height || 40} onChange={(e) => u('height', parseInt(e.target.value))} className="bg-white/5 border-white/10 text-white mt-1 rounded-lg w-24" />
    </div>
  );
}

export function BookingEditor({ block, updateBlock }) {
  const u = (f, v) => updateBlock(block.id, f, v);
  return (
    <div className="space-y-3">
      <div><Label className="text-[#9CA3AF] text-xs">Booking Title</Label><Input value={block.content.title || ''} onChange={(e) => u('title', e.target.value)} className="bg-white/5 border-white/10 text-white mt-1 rounded-lg" /></div>
      <div className="flex items-center gap-3">
        <label className="flex items-center gap-1.5 text-xs text-[#94A3B8]"><input type="checkbox" checked={block.content.show_prices !== false} onChange={(e) => u('show_prices', e.target.checked)} className="rounded bg-white/5 border-white/10" /> Show prices</label>
        <label className="flex items-center gap-1.5 text-xs text-[#94A3B8]"><input type="checkbox" checked={block.content.show_duration !== false} onChange={(e) => u('show_duration', e.target.checked)} className="rounded bg-white/5 border-white/10" /> Show duration</label>
      </div>
    </div>
  );
}

export function DonationsEditor({ block, updateBlock }) {
  const u = (f, v) => updateBlock(block.id, f, v);
  const [funds, setFunds] = React.useState([]);
  const [loadingFunds, setLoadingFunds] = React.useState(true);

  React.useEffect(() => {
    let live = true;
    const token = localStorage.getItem('token');
    const API = process.env.REACT_APP_BACKEND_URL + '/api';
    axios.get(`${API}/builder/donations/funds`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { if (live) setFunds(r.data || []); })
      .catch(() => {})
      .finally(() => { if (live) setLoadingFunds(false); });
    return () => { live = false; };
  }, []);

  // Iter 171 — When the user picks a fund, hydrate the block's `amounts`,
  // disclaimer, custom-amounts, cover-fees defaults from the fund so the
  // canvas instantly reflects the linked settings without a save round-trip.
  const pickFund = (fundId) => {
    const fund = funds.find((f) => f.id === fundId);
    if (!fund) {
      u('fund_id', '');
      return;
    }
    u('fund_id', fund.id);
    u('amounts', (fund.amounts || []).map((a) => (typeof a === 'object' ? a : { value: a, description: '' })));
    u('custom_amounts_enabled', !!fund.custom_amounts_enabled);
    u('cover_fees_enabled', !!fund.cover_fees_enabled);
    u('cover_fees_description', fund.cover_fees_description || '');
    u('disclaimer', fund.disclaimer || '');
  };

  const LAYOUTS = [
    { id: 'centered',    label: 'Centered',         hint: 'Form on a soft background' },
    { id: 'side-image',  label: 'Form + side image',hint: 'Image on the right' },
    { id: 'image-bg',    label: 'Image background', hint: 'Floating form on a photo' },
    { id: 'image-hero',  label: 'Image hero',       hint: 'Wide image on top, form below' },
    { id: 'marquee',     label: 'Marquee banner',   hint: 'Scrolling headline + form' },
    { id: 'stats',       label: 'Impact stats',     hint: 'Stats grid + form' },
  ];

  const settingsHref = block.content.fund_id
    ? `/website-builder/settings/donations/${block.content.fund_id}`
    : '/website-builder/settings/donations';

  return (
    <div className="space-y-4">
      <div>
        <Label className="text-[#9CA3AF] text-[10px] uppercase tracking-wider mb-1.5 block">Layout</Label>
        <div className="grid grid-cols-2 gap-1.5" data-testid="donations-layout-picker">
          {LAYOUTS.map((L) => {
            const active = (block.layout || 'centered') === L.id;
            return (
              <button
                key={L.id}
                type="button"
                onClick={() => updateBlock(block.id, '_layout', L.id)}
                className={`text-left p-2.5 rounded-xl border transition-all ${active ? 'border-violet-400/60 bg-violet-500/[0.08]' : 'border-white/10 bg-white/[0.02] hover:border-white/20'}`}
                data-testid={`donations-layout-${L.id}`}
              >
                <div className="text-[11.5px] font-semibold text-white">{L.label}</div>
                <div className="text-[10px] text-white/50 mt-0.5">{L.hint}</div>
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <Label className="text-[#9CA3AF] text-[10px] uppercase tracking-wider mb-1.5 block">Linked fund</Label>
        <select
          value={block.content.fund_id || ''}
          onChange={(e) => pickFund(e.target.value)}
          disabled={loadingFunds}
          data-testid="donations-fund-picker"
          className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-violet-400"
        >
          <option value="" className="bg-[#15152E]">— None (use inline amounts) —</option>
          {funds.map((f) => (
            <option key={f.id} value={f.id} className="bg-[#15152E]">{f.name}</option>
          ))}
        </select>
        <a
          href={settingsHref}
          data-testid="donations-edit-fund-link"
          className="mt-2 inline-flex items-center gap-1.5 text-[11px] text-[#A78BFA] hover:text-white"
        >
          Edit amounts &amp; settings →
        </a>
      </div>

      <div><Label className="text-[#9CA3AF] text-xs">Title</Label><Input value={block.content.title || ''} onChange={(e) => u('title', e.target.value)} className="bg-white/5 border-white/10 text-white mt-1 rounded-lg" /></div>
      <div><Label className="text-[#9CA3AF] text-xs">Subtitle</Label><Input value={block.content.subtitle || ''} onChange={(e) => u('subtitle', e.target.value)} className="bg-white/5 border-white/10 text-white mt-1 rounded-lg" /></div>
      {!block.content.fund_id && (
        <div>
          <Label className="text-[#9CA3AF] text-xs">Suggested amounts (comma-separated)</Label>
          <Input
            value={(block.content.amounts || []).map((a) => (typeof a === 'object' ? a.value : a)).join(', ')}
            onChange={(e) => u('amounts', e.target.value.split(',').map((v) => parseInt(v.trim(), 10)).filter((v) => !isNaN(v)).map((v) => ({ value: v, description: '' })))}
            className="bg-white/5 border-white/10 text-white mt-1 rounded-lg"
            placeholder="25, 50, 100"
            data-testid="donations-amounts-input"
          />
          <p className="text-[10px] text-white/40 mt-1">Or link a fund above for richer settings (custom amounts, cover-fees, recurring).</p>
        </div>
      )}
    </div>
  );
}

export function FeaturedProductsEditor({ block, updateBlock, allProducts }) {
  const u = (f, v) => updateBlock(block.id, f, v);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const featured = block.content.featured_product_ids || [];
  const featuredProducts = featured.map(id => allProducts.find(p => p.id === id)).filter(Boolean);
  const available = allProducts.filter(p => !featured.includes(p.id));
  const handleDragEnd = (e) => {
    const { active, over } = e;
    if (active.id !== over?.id) {
      const oldIdx = featured.indexOf(active.id);
      const newIdx = featured.indexOf(over.id);
      u('featured_product_ids', arrayMove(featured, oldIdx, newIdx));
    }
  };
  return (
    <div className="space-y-3">
      <div><Label className="text-[#9CA3AF] text-xs">Section Title</Label><Input value={block.content.title || ''} onChange={(e) => u('title', e.target.value)} className="bg-white/5 border-white/10 text-white mt-1 rounded-lg" /></div>
      <div><Label className="text-[#9CA3AF] text-xs">Subtitle</Label><Input value={block.content.subtitle || ''} onChange={(e) => u('subtitle', e.target.value)} className="bg-white/5 border-white/10 text-white mt-1 rounded-lg" /></div>
      <div><Label className="text-[#9CA3AF] text-[10px] uppercase tracking-wider">Featured Products ({featured.length})</Label>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={featured} strategy={verticalListSortingStrategy}>
            <div className="space-y-1 mt-1">
              {featuredProducts.map(p => <SortableFeaturedProduct key={p.id} product={p} onRemove={() => u('featured_product_ids', featured.filter(id => id !== p.id))} />)}
            </div>
          </SortableContext>
        </DndContext>
      </div>
      {available.length > 0 && <div><Label className="text-[#9CA3AF] text-[10px]">Add Products</Label>
        <div className="space-y-1 mt-1 max-h-32 overflow-y-auto">
          {available.slice(0, 10).map(p => (
            <button key={p.id} onClick={() => u('featured_product_ids', [...featured, p.id])} className="flex items-center gap-2 w-full p-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-left">
              {p.images?.[0] && <img src={p.images[0]} alt="" className="w-6 h-6 rounded object-cover" />}
              <span className="text-[11px] text-[#94A3B8] flex-1 truncate">{p.name}</span>
              <span className="text-[10px] text-[#00CC99]">${p.price}</span>
            </button>
          ))}
        </div>
      </div>}
    </div>
  );
}

export function FormBlockEditor({ block, updateBlock }) {
  const u = (f, v) => updateBlock(block.id, f, v);
  const [forms, setForms] = useState([]);
  useEffect(() => {
    axios.get(`${API}/forms`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }).then(r => setForms(r.data || [])).catch(() => {});
  }, []);
  const selectedForm = forms.find(f => f.id === block.content.form_id);
  return (
    <div className="space-y-3" data-testid="form-block-editor">
      <div><Label className="text-[#9CA3AF] text-xs">Select Form</Label>
        <select value={block.content.form_id || ''} onChange={(e) => { const f = forms.find(f => f.id === e.target.value); u('form_id', e.target.value); if (f) { u('title', f.title); u('description', f.description); u('fields', f.fields); u('header_image_url', f.header_image_url); } }} className="w-full bg-white/5 border border-white/10 text-white rounded-lg px-3 py-2 text-sm mt-1" data-testid="form-select">
          <option value="">Choose a form...</option>
          {forms.filter(f => f.is_active).map(f => <option key={f.id} value={f.id}>{f.title} ({f.submission_count || 0} submissions)</option>)}
        </select>
      </div>
      {selectedForm && <div className="p-2 rounded-xl bg-[#8B5CF6]/5 border border-[#8B5CF6]/20">
        <p className="text-xs font-medium text-[#A78BFA]">{selectedForm.title}</p>
        <p className="text-[10px] text-slate-400">{selectedForm.fields?.length || 0} fields</p>
      </div>}
    </div>
  );
}

export function EmbedBlockEditor({ block, updateBlock }) {
  const u = (f, v) => updateBlock(block.id, f, v);
  const [detecting, setDetecting] = useState(false);
  const detectEmbed = async (url) => {
    u('url', url);
    if (!url.trim()) return;
    setDetecting(true);
    try {
      const res = await axios.post(`${API}/embed/metadata`, { url }, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      const meta = res.data;
      u('embed_type', meta.embed_type); u('source', meta.source); u('embeddable', meta.embeddable); u('embed_url', meta.embed_url); u('thumbnail', meta.thumbnail || '');
    } catch {} finally { setDetecting(false); }
  };
  const c = block.content || {};
  const sourceLabel = c.source || (c.embed_type === 'youtube' ? 'YouTube' : c.embed_type === 'spotify' ? 'Spotify' : '');
  return (
    <div className="space-y-3" data-testid="embed-block-editor">
      <div><Label className="text-[#9CA3AF] text-xs">Paste URL</Label>
        <div className="relative mt-1"><Globe size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
          <Input value={c.url || ''} onChange={(e) => detectEmbed(e.target.value)} placeholder="https://youtube.com/watch?v=... or any URL" className="bg-white/5 border-white/10 text-white pl-9 rounded-lg" data-testid="embed-url-input" />
          {detecting && <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8B5CF6] animate-spin" />}
        </div>
      </div>
      {sourceLabel && <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-[#8B5CF6]/5 border border-[#8B5CF6]/20">
        <span className="text-[10px] font-semibold text-[#A78BFA] uppercase">{sourceLabel}</span>
        {c.embeddable && <span className="text-[10px] text-[#00CC99]">Embeddable</span>}
        {!c.embeddable && c.embed_type !== 'link' && <span className="text-[10px] text-[#F59E0B]">Link only</span>}
      </div>}
      {c.thumbnail && <img src={c.thumbnail} alt="" className="w-full h-24 object-cover rounded-xl border border-white/10" />}
      <div><Label className="text-[#9CA3AF] text-xs">Title (optional)</Label><Input value={c.title || ''} onChange={(e) => u('title', e.target.value)} placeholder="Embed title" className="bg-white/5 border-white/10 text-white mt-1 rounded-lg" /></div>
      <div><Label className="text-[#9CA3AF] text-xs">Layout</Label>
        <select value={c.layout || 'wide'} onChange={(e) => u('layout', e.target.value)} className="w-full bg-white/5 border border-white/10 text-white rounded-lg px-3 py-2 text-sm mt-1"><option value="wide">Wide (full width)</option><option value="compact">Compact (card)</option></select>
      </div>
      <ButtonsEditor buttons={c.buttons || []} onChange={(v) => u('buttons', v)} blockId={block.id} />
    </div>
  );
}

export function SingleImageBlockEditor({ block, updateBlock }) {
  const u = (f, v) => updateBlock(block.id, f, v);
  const c = block.content || {};
  const shapes = [{ value: 'rectangle', label: 'Rectangle', icon: RectangleHorizontal }, { value: 'square', label: 'Square', icon: Square }, { value: 'circle', label: 'Circle', icon: Circle }];
  const alignments = [{ value: 'left', icon: AlignLeft }, { value: 'center', icon: AlignCenter }, { value: 'right', icon: AlignRight }];
  return (
    <div className="space-y-3" data-testid="single-image-editor">
      <ImageUploader value={c.image_url} onChange={(v) => u('image_url', v)} label="Image" blockId={block.id + '-simg'} linkValue={c.linked_url} onLinkChange={(v) => u('linked_url', v)} />
      <div><Label className="text-[#9CA3AF] text-xs">Caption</Label><Input value={c.caption || ''} onChange={(e) => u('caption', e.target.value)} placeholder="Image caption" className="bg-white/5 border-white/10 text-white mt-1 rounded-lg" /></div>
      <div><Label className="text-[#9CA3AF] text-xs mb-1.5 block">Shape</Label>
        <div className="flex gap-2">{shapes.map(s => { const Icon = s.icon; return (<button key={s.value} onClick={() => u('shape', s.value)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs border transition-all ${(c.shape || 'rectangle') === s.value ? 'bg-[#8B5CF6]/20 text-[#A78BFA] border-[#8B5CF6]/30' : 'bg-white/5 text-[#64748B] border-white/5 hover:border-white/10'}`} data-testid={`shape-${s.value}`}><Icon size={12} /> {s.label}</button>); })}</div>
      </div>
      <div><Label className="text-[#9CA3AF] text-xs mb-1.5 block">Alignment</Label>
        <div className="flex gap-2">{alignments.map(a => { const Icon = a.icon; return (<button key={a.value} onClick={() => u('alignment', a.value)} className={`p-2 rounded-lg border transition-all ${(c.alignment || 'center') === a.value ? 'bg-[#8B5CF6]/20 text-[#A78BFA] border-[#8B5CF6]/30' : 'bg-white/5 text-[#64748B] border-white/5 hover:border-white/10'}`} data-testid={`align-${a.value}`}><Icon size={14} /></button>); })}</div>
      </div>
      <ButtonsEditor buttons={c.buttons || []} onChange={(v) => u('buttons', v)} blockId={block.id} />
    </div>
  );
}


export function MarqueeEditor({ block, updateBlock }) {
  const u = (f, v) => updateBlock(block.id, f, v);
  const c = block.content || {};
  const words = Array.isArray(c.words) ? c.words : [];
  const [draft, setDraft] = useState(words.join('\n'));
  useEffect(() => { setDraft(words.join('\n')); /* eslint-disable-next-line */ }, [block.id]);
  const commitWords = (text) => {
    setDraft(text);
    const arr = text.split('\n').map(s => s.trim()).filter(Boolean);
    u('words', arr);
  };
  const speeds = [
    { value: 'slow', label: 'Slow', sec: 50 },
    { value: 'medium', label: 'Medium', sec: 30 },
    { value: 'fast', label: 'Fast', sec: 15 },
  ];
  const separators = [
    { value: 'dot', label: '•' },
    { value: 'star', label: '★' },
    { value: 'slash', label: '/' },
    { value: 'dash', label: '—' },
    { value: 'none', label: 'None' },
  ];
  const sizes = [
    { value: 'md', label: 'M' },
    { value: 'lg', label: 'L' },
    { value: 'xl', label: 'XL' },
    { value: '2xl', label: '2XL' },
    { value: '3xl', label: '3XL' },
  ];
  return (
    <div className="space-y-3" data-testid="marquee-editor">
      <div>
        <Label className="text-[#9CA3AF] text-xs">Words (one per line)</Label>
        <Textarea
          value={draft}
          onChange={(e) => commitWords(e.target.value)}
          rows={6}
          placeholder={'Indigenous Made\nAuthentic\nHandcrafted\nPremium'}
          className="bg-white/5 border-white/10 text-white mt-1 rounded-lg font-mono text-sm"
          data-testid="marquee-words"
        />
        <p className="text-[10px] text-[#64748B] mt-1">Each line becomes one word/phrase in the moving ticker.</p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-[#9CA3AF] text-xs mb-1.5 block">Speed</Label>
          <div className="flex gap-1.5">
            {speeds.map(s => (
              <button key={s.value} onClick={() => u('speed', s.value)}
                className={`flex-1 px-2 py-1.5 rounded-lg text-xs border transition-all ${(c.speed || 'medium') === s.value ? 'bg-[#8B5CF6]/20 text-[#A78BFA] border-[#8B5CF6]/30' : 'bg-white/5 text-[#64748B] border-white/5 hover:border-white/10'}`}
                data-testid={`marquee-speed-${s.value}`}>{s.label}</button>
            ))}
          </div>
        </div>
        <div>
          <Label className="text-[#9CA3AF] text-xs mb-1.5 block">Direction</Label>
          <div className="flex gap-1.5">
            {['left','right'].map(d => (
              <button key={d} onClick={() => u('direction', d)}
                className={`flex-1 px-2 py-1.5 rounded-lg text-xs border transition-all ${(c.direction || 'left') === d ? 'bg-[#8B5CF6]/20 text-[#A78BFA] border-[#8B5CF6]/30' : 'bg-white/5 text-[#64748B] border-white/5 hover:border-white/10'}`}
                data-testid={`marquee-direction-${d}`}>{d === 'left' ? '← Left' : 'Right →'}</button>
            ))}
          </div>
        </div>
      </div>

      <div>
        <Label className="text-[#9CA3AF] text-xs mb-1.5 block">Separator between words</Label>
        <div className="flex gap-1.5 flex-wrap">
          {separators.map(s => (
            <button key={s.value} onClick={() => u('separator', s.value)}
              className={`px-3 py-1.5 rounded-lg text-sm border transition-all ${(c.separator || 'dot') === s.value ? 'bg-[#8B5CF6]/20 text-[#A78BFA] border-[#8B5CF6]/30' : 'bg-white/5 text-[#64748B] border-white/5 hover:border-white/10'}`}
              data-testid={`marquee-sep-${s.value}`}>{s.label}</button>
          ))}
        </div>
      </div>

      <div>
        <Label className="text-[#9CA3AF] text-xs mb-1.5 block">Text size</Label>
        <div className="flex gap-1.5">
          {sizes.map(s => (
            <button key={s.value} onClick={() => u('font_size', s.value)}
              className={`flex-1 px-2 py-1.5 rounded-lg text-xs border transition-all ${(c.font_size || 'lg') === s.value ? 'bg-[#8B5CF6]/20 text-[#A78BFA] border-[#8B5CF6]/30' : 'bg-white/5 text-[#64748B] border-white/5 hover:border-white/10'}`}
              data-testid={`marquee-size-${s.value}`}>{s.label}</button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-[#9CA3AF] text-xs">Background</Label>
          <input type="color" value={c.bg_color || '#0F172A'} onChange={(e) => u('bg_color', e.target.value)} className="mt-1 w-full h-9 rounded-lg bg-transparent border border-white/10" data-testid="marquee-bg" />
        </div>
        <div>
          <Label className="text-[#9CA3AF] text-xs">Text color</Label>
          <input type="color" value={c.text_color || '#FFFFFF'} onChange={(e) => u('text_color', e.target.value)} className="mt-1 w-full h-9 rounded-lg bg-transparent border border-white/10" data-testid="marquee-color" />
        </div>
      </div>

      <div className="flex items-center gap-3 pt-1">
        <label className="flex items-center gap-2 text-[#9CA3AF] text-xs">
          <input type="checkbox" checked={!!c.italic} onChange={(e) => u('italic', e.target.checked)} data-testid="marquee-italic" />
          Italic
        </label>
        <label className="flex items-center gap-2 text-[#9CA3AF] text-xs">
          <input type="checkbox" checked={(c.weight || 'bold') === 'bold'} onChange={(e) => u('weight', e.target.checked ? 'bold' : 'regular')} data-testid="marquee-bold" />
          Bold
        </label>
      </div>
    </div>
  );
}
